import React, { useEffect, useState } from "react";
import { Box, Button, Flex, Heading, Icon, IconButton, Text, Tooltip, VStack, useDisclosure } from "@chakra-ui/react";
import { HiOutlineArrowLeft, HiOutlineMoon, HiOutlinePlus, HiOutlineSun, HiOutlineCog6Tooth } from "react-icons/hi2";
import { useLocation, useNavigate } from "react-router-dom";
import { useClassroomToast } from "./classroomToast";
import CourseContent from "./content/CourseContent";
import MarkingPanel from "./marking/MarkingPanel";
import { useCourseContent } from "./content/useCourseContent";
import CreateCustomButton from "./CreateCustomButton";
import AddSegmentModal from "./AddSegmentModal";
import AttachmentsSection from "./AttachmentsSection";
import ManageClassroomModal from "./ManageClassroomModal";
import { BASE_API_URL } from "../Components/config";
import { c } from "./classroomColors";
import { brand } from "./standeTokens";
import LiveCard from "./space/LiveCard";
import SessionList from "./space/SessionList";
import SessionSheet from "./space/SessionSheet";
import SessionForm from "./space/SessionForm";
import { ContentPanel, PeoplePanel, WorkPanel } from "./space/SidePanels";
import { useSpaceSessions } from "./space/useSpaceSessions";
import { useSpaceWork } from "./space/useSpaceWork";
import { useStudentCount } from "./space/useStudentCount";
import { quietButton, inkButton } from "./space/parts";
import { enterFullscreen } from "./inclass/useFullscreen";
import "./classroom-theme.css";

/**
 * A space: where a teacher plans and runs sessions, keeps the learning
 * content, and sets and marks work — and where students find all of it.
 *
 * One page, in the order it is needed: what is happening now (or next),
 * then the sessions — coming, being planned, held — with the work and the
 * content beside them. A session opens in a sheet over the page; the work
 * and the content open in full from their panels. This component only
 * arranges the parts: each owns its data through its own hook.
 *
 * (The route and the file keep the old "classroom" name, so every link in
 * keeps working; what people read says "space".)
 */
const InsideClassroom = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { courseGuid, classroomName, userType } = location.state || {};
  const isInstructor = userType === "INSTRUCTOR";

  const [name, setName] = useState(classroomName);
  const [subjectGuid, setSubjectGuid] = useState(null);
  // home, or one of the full views opened from its panels
  const [view, setView] = useState("home");
  const [openGuid, setOpenGuid] = useState(null);
  const [editing, setEditing] = useState(null);
  const form = useDisclosure();
  const manage = useDisclosure();
  const addContent = useDisclosure();
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("classroomColorMode");
    return saved ? saved === "dark" : true;
  });
  const toast = useClassroomToast(isDark);
  // How the sessions show: a board, a list or a week. Remembered for this
  // browser; the board to start with, where there is room for it.
  const [sessionView, setSessionView] = useState(() => {
    try {
      const saved = localStorage.getItem("spaceSessionView");
      if (["board", "list", "week"].includes(saved)) return saved;
    } catch {
      /* no storage: the default */
    }
    return window.innerWidth >= 992 ? "board" : "list";
  });
  const chooseSessionView = (next) => {
    setSessionView(next);
    try {
      localStorage.setItem("spaceSessionView", next);
    } catch {
      /* it just isn't remembered */
    }
  };

  const sessions = useSpaceSessions(courseGuid);
  const content = useCourseContent(courseGuid);
  const work = useSpaceWork({ courseGuid, isInstructor });
  const studentCount = useStudentCount(courseGuid, isInstructor);
  const open = (sessions.sessions || []).find((s) => s.guid === openGuid) || null;

  useEffect(() => {
    if (!courseGuid) return;
    fetch(`${BASE_API_URL}/api/v1/courses/${courseGuid}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setSubjectGuid(data.subject_guid))
      .catch(() => {});
  }, [courseGuid]);

  const toggleColorMode = () =>
    setIsDark((prev) => {
      localStorage.setItem("classroomColorMode", prev ? "light" : "dark");
      return !prev;
    });

  const enterRoom = (session) => {
    // The room fills the screen; a browser allows that only on the click itself.
    enterFullscreen();
    navigate("/classroom/in-class", {
      state: {
        courseGuid,
        classroomName: name,
        sessionGuid: session.guid,
        sessionName: session.name,
        userType,
        // Opening the room doesn't start the session; the teacher goes live
        // from inside it.
        sessionState: session.state,
      },
    });
  };

  const submitSession = async (values) => {
    if (editing) {
      await sessions.update(editing.guid, values);
      toast({ title: "Session saved", status: "success" });
      return;
    }
    const created = await sessions.create(values);
    toast({ title: values.scheduledAt ? "Session scheduled" : "Draft saved", description: created.name, status: "success" });
  };

  const deleteSession = async (session) => {
    try {
      await sessions.remove(session.guid);
      setOpenGuid(null);
      toast({ title: "Session deleted", description: session.name, status: "success" });
    } catch (e) {
      console.error(e);
      toast({ title: "Couldn't delete the session", description: "Try again.", status: "error" });
    }
  };

  const toggleQuestions = async (session, on) => {
    try {
      await sessions.update(session.guid, { questionsEnabled: on });
    } catch {
      toast({ title: "Couldn't change that", description: "Try again.", status: "error" });
    }
  };

  const editSession = (session) => {
    setEditing(session);
    form.onOpen();
  };

  // Dragged back to Drafts on the board: the date goes, the rest stays.
  const unschedule = async (session) => {
    try {
      await sessions.update(session.guid, { scheduledAt: "" });
      toast({ title: "Moved back to drafts", description: session.name, status: "success" });
    } catch {
      toast({ title: "Couldn't move it", description: "Try again.", status: "error" });
    }
  };

  const newSession = () => {
    setEditing(null);
    form.onOpen();
  };

  const backToSpaces = () =>
    navigate(isInstructor ? "/classroom/teacher/dashboard" : "/classroom/student/dashboard");

  return (
    <Box
      className="stande-classroom"
      data-theme={isDark ? "dark" : "light"}
      bg={c.page}
      minH="100vh"
      px={{ base: "16px", md: "40px" }}
      py={{ base: "20px", md: "32px" }}
      transition="background 0.2s"
    >
      <Box maxW={view === "home" ? "1400px" : "1240px"} mx="auto">
        {/* Header: back, which space this is, and its tools. */}
        <Flex align="center" gap="16px" mb="26px" wrap="wrap">
          <Flex as="button" type="button" align="center" gap="8px" color={c.textSub} _hover={{ color: c.text }}
            onClick={view === "home" ? backToSpaces : () => setView("home")}>
            <Flex boxSize="30px" borderRadius="full" align="center" justify="center" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
              <Icon as={HiOutlineArrowLeft} boxSize="14px" />
            </Flex>
            <Text fontSize="14px">{view === "home" ? "Spaces" : name}</Text>
          </Flex>

          <Flex flex="1" justify="center" minW="220px">
            <Flex align="center" gap="12px" px="12px" py="8px" pr="18px" borderRadius="16px" bg={c.surface} border="1px solid" borderColor={c.borderSoft} maxW="100%">
              <Box boxSize="34px" borderRadius="10px" bg={brand(isDark).lightGreen} border="1px solid" borderColor={c.borderSoft} flexShrink={0} />
              <Box minW={0}>
                <Heading fontSize="16px" fontWeight="600" color={c.text} noOfLines={1}>{name}</Heading>
                <Text fontSize="12px" color={c.textSub}>{view === "home" ? "Space" : view === "content" ? "Content" : "Work"}</Text>
              </Box>
            </Flex>
          </Flex>

          <Flex align="center" gap="8px">
            <Tooltip label={isDark ? "Light mode" : "Dark mode"} hasArrow>
              <IconButton aria-label="Switch light and dark" icon={<Icon as={isDark ? HiOutlineSun : HiOutlineMoon} boxSize="17px" />}
                onClick={toggleColorMode} {...quietButton} size="sm" h="38px" w="38px" borderRadius="12px" />
            </Tooltip>
            {isInstructor && (
              <>
                <Button {...quietButton} h="38px" px="14px" borderRadius="12px" fontSize="13px" leftIcon={<Icon as={HiOutlineCog6Tooth} boxSize="16px" />} onClick={manage.onOpen}>
                  Manage
                </Button>
                <Button {...inkButton} h="38px" px="14px" borderRadius="12px" fontSize="13px" leftIcon={<Icon as={HiOutlinePlus} boxSize="15px" />} onClick={newSession}>
                  New session
                </Button>
              </>
            )}
          </Flex>
        </Flex>

        {view === "home" && (
          <VStack align="stretch" spacing="20px">
            {/* The top: a live session beside the work while it runs; the work alone otherwise. */}
            <Box display="grid" gridTemplateColumns={{ base: "minmax(0, 1fr)", lg: sessions.live.length ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)" }}
              gap="14px" alignItems="stretch">
              {sessions.live.length > 0 && (
                <VStack align="stretch" spacing="14px" minW={0}>
                  {sessions.live.map((s) => (
                    <Box key={s.guid} flex="1">
                      <LiveCard session={s} isInstructor={isInstructor} studentCount={studentCount}
                        onEnter={enterRoom} onOpen={(x) => setOpenGuid(x.guid)} />
                    </Box>
                  ))}
                </VStack>
              )}
              <WorkPanel work={work} isInstructor={isInstructor} onOpen={() => setView("work")} compact={sessions.live.length > 0}
                setWork={isInstructor ? <CreateCustomButton subjectGuid={subjectGuid} courseGuid={courseGuid} classroomName={name} isDark={isDark} /> : null} />
            </Box>
            {sessions.error ? (
              <Text fontSize="13px" color={c.textSub}>{sessions.error}</Text>
            ) : (
              <SessionList
                groups={sessions}
                isInstructor={isInstructor}
                isDark={isDark}
                selectedGuid={openGuid}
                onOpen={(s) => setOpenGuid(s.guid)}
                onCreate={newSession}
                view={sessionView}
                onView={chooseSessionView}
                studentCount={studentCount}
                onEnter={enterRoom}
                onSchedule={editSession}
                onUnschedule={unschedule}
              />
            )}
            <Box display="grid" gridTemplateColumns={{ base: "minmax(0, 1fr)", md: isInstructor ? "minmax(0, 2fr) minmax(0, 1fr)" : "minmax(0, 1fr)" }}
              gap="14px" alignItems="start">
              <ContentPanel modules={content.modules} onOpen={() => setView("content")} />
              <PeoplePanel count={studentCount} isInstructor={isInstructor} onManage={manage.onOpen} />
            </Box>
          </VStack>
        )}

        {view === "work" && (
          <Box bg={c.surface} border="1px solid" borderColor={c.borderSoft} borderRadius="18px" p={{ base: "16px", md: "28px" }}>
            <VStack align="stretch" spacing="26px">
              <MarkingPanel courseGuid={courseGuid} isInstructor={isInstructor} isDark={isDark} />
              <AttachmentsSection courseGuid={courseGuid} isInstructor={isInstructor} isDark={isDark} />
            </VStack>
          </Box>
        )}

        {view === "content" && (
          <Box bg={c.surface} border="1px solid" borderColor={c.borderSoft} borderRadius="18px" p={{ base: "16px", md: "28px" }}>
            <CourseContent content={content} courseGuid={courseGuid} isInstructor={isInstructor} isDark={isDark}
              onOpenAdvanced={isInstructor ? addContent.onOpen : null} />
          </Box>
        )}
      </Box>

      <SessionSheet
        session={open}
        isOpen={Boolean(open)}
        onClose={() => setOpenGuid(null)}
        courseGuid={courseGuid}
        subjectGuid={subjectGuid}
        spaceName={name}
        isInstructor={isInstructor}
        isDark={isDark}
        onEdit={editSession}
        onDelete={deleteSession}
        onEnter={enterRoom}
        onToggleQuestions={toggleQuestions}
        onChanged={sessions.refresh}
      />
      <SessionForm isOpen={form.isOpen} onClose={form.onClose} onSubmit={submitSession} session={editing} isDark={isDark} />
      <ManageClassroomModal isOpen={manage.isOpen} onClose={manage.onClose} courseGuid={courseGuid} classroomName={name}
        isDark={isDark} onCourseUpdated={setName} />
      <AddSegmentModal isOpen={addContent.isOpen} onClose={addContent.onClose} onSuccess={content.refresh} courseGuid={courseGuid} isDark={isDark} />
    </Box>
  );
};

export default InsideClassroom;
